import { fetchCurrentUser } from '../../ducks/user.duck';
import { IMAGE_FIELDS, IMAGE_VARIANTS, LISTING_ID, SUPPLIER_ID } from '../../util/constants';
import { showUser } from '../ProfilePage/ProfilePage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { getImageVariantConfigs } from '../../util/helperFunctions';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //
export const SHOW_LISTING_REQUEST = 'app/SupplierCostEstimationToolPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/SupplierCostEstimationToolPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/SupplierCostEstimationToolPage/SHOW_LISTING_ERROR';

const initialState = {
  showListingInProgress: false,
  showListingError: null,
  listingAuthorId: null,
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SHOW_LISTING_REQUEST:
      return { ...state, showListingInProgress: true, showListingError: null };
    case SHOW_LISTING_SUCCESS:
      return {
        ...state,
        showListingInProgress: false,
        listingAuthorId: payload.listingAuthorId,
        listingTitle: payload.listingTitle,
        showListingError: null,
      };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingInProgress: false, showListingError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const listingAuthorIdSelector = state =>
  state.SupplierCostEstimationToolPage.listingAuthorId;
export const listingTitleSelector = state => state.SupplierCostEstimationToolPage.listingTitle;
// ================ Action creators ================ //

export const showListingRequest = () => ({
  type: SHOW_LISTING_REQUEST,
});
export const showListingSuccess = (listingAuthorId, listingTitle) => ({
  type: SHOW_LISTING_SUCCESS,
  payload: { listingAuthorId, listingTitle },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  dispatch(showListingRequest());
  // Extract image configuration
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = IMAGE_VARIANTS.PREFIX,
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  try {
    // Build API parameters
    const params = {
      id: listingId,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        ...IMAGE_FIELDS,
        `variants.${variantPrefix}`, // Cropped variants
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      ...getImageVariantConfigs(variantPrefix, aspectRatio),
    };

    const response = await sdk.listings.show(params);
    const listing = response?.data?.data;
    const listingTitle = listing?.attributes?.title;
    const authorId = listing?.relationships?.author?.data?.id?.uuid;
    if (authorId) {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showListingSuccess(authorId, listingTitle));
    }
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  try {
    const queryParams = new URLSearchParams(search);
    const validateAndDispatch = (id, action, type) => {
      if (id?.trim()) {
        try {
          const uuid = new UUID(id);
          dispatch(action(uuid, config));
        } catch (error) {
          console.error(`Invalid UUID format for ${type}:`, id, error);
        }
      } else {
        console.warn(`No valid ${type} found in query parameters:`, id);
      }
    };

    validateAndDispatch(queryParams.get(LISTING_ID), showListing, LISTING_ID);
    validateAndDispatch(queryParams.get(SUPPLIER_ID), showUser, SUPPLIER_ID);
    await dispatch(fetchCurrentUser());

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};
